import { X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface SheetProps {
  open: boolean
  title: string
  children: ReactNode
  onOpenChange: (open: boolean) => void
  className?: string
}

export const Sheet = ({ open, title, children, onOpenChange, className }: SheetProps) => {
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      return undefined
    }

    setVisible(false)
    if (!mounted) {
      return undefined
    }

    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [mounted, open])

  useEffect(() => {
    if (!mounted || !open) return undefined

    const firstFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setVisible(true))
    })

    return () => window.cancelAnimationFrame(firstFrame)
  }, [mounted, open])

  if (!mounted) return null

  return (
    <div className="fixed inset-0 z-50">
      <button
        className={cn(
          'absolute inset-0 bg-foreground/20 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        type="button"
        aria-label="关闭"
        onClick={() => onOpenChange(false)}
      />
      <aside
        className={cn(
          'absolute right-0 top-0 flex h-full w-full max-w-[480px] flex-col border-l border-border bg-card shadow-lg transition-transform duration-200 ease-out',
          visible ? 'translate-x-0' : 'translate-x-full',
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="flex h-14 items-center justify-between border-b border-border px-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <Button variant="ghost" size="icon" aria-label="关闭" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
          </Button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-4">{children}</div>
      </aside>
    </div>
  )
}
